import React, { useEffect, useRef } from 'react';
import { useLatest } from 'ahooks';
import useCOS from '@gp/hooks/useCos/useCOS';
import { ENV } from '@home/_config/config';
import { COS_USE_ACCELERATE_ENVS } from '@intelligence/_config/config';
import { fetchReportTencentQCouldMassage } from '@intelligence/services/cos';
import { buildHTMLReportPreviewUrl } from '@home/utils/reportPreview';
import { getAuthorization } from '@/utils/utils';
import { pageReportTypeConfig } from '@intelligence/_config/reportConfig';
import { getFileData } from '../utils';
import useReportDeletedCheck from './useReportDeletedCheck';

import type { AxiosResponse } from 'axios';

// 对齐移动端：iframe 无法带 Authorization 头，后端从 refreshToken cookie 读登录态
const STUDIO_HTML_PREVIEW_TOKEN_COOKIE = 'refreshToken';

const writeStudioPreviewTokenCookie = (token: string): void => {
  // 与移动端一致：token 原样写入。encodeURIComponent 会导致后端校验失败（code 20001）
  document.cookie = `${STUDIO_HTML_PREVIEW_TOKEN_COOKIE}=${token}; path=/`;
};

const clearStudioPreviewTokenCookie = (): void => {
  document.cookie = `${STUDIO_HTML_PREVIEW_TOKEN_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const fetchReportGZTencentQCouldMassage = async () => {
  const res = await fetchReportTencentQCouldMassage({ cos_config: 'guangzhou' });
  return res?.data;
};

const DEFAULT_COS_OPTIONS: IUseCOSOptions = {
  useAccelerate: false,
  baseCOSConfigFetcher: fetchReportGZTencentQCouldMassage,
  authorizationFetcher: fetchReportGZTencentQCouldMassage,
};

export default function useHTMLReportPreview(props: {
  executable: boolean;
  cosOptions?: IUseCOSOptions;
  reportFileKey?: string;
  originalFileKey?: string;
  systemId: string;
  studioId?: string;
  resourceId?: string;
  lang?: string;
  fetchPDFUrl: () => AxiosResponse<Blob>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setFileSuffix: React.Dispatch<React.SetStateAction<string>>;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
  setPreviewUrl: React.Dispatch<React.SetStateAction<string>>;
  onError?: (exception: unknown) => void;
  onDeleted?: () => void;
}) {
  const {
    executable,
    cosOptions = DEFAULT_COS_OPTIONS,
    reportFileKey,
    originalFileKey,
    systemId,
    studioId = '',
    resourceId = '',
    lang = 'en',
    fetchPDFUrl,
    setIsLoading,
    setIsError,
    setFileSuffix,
    setUrl,
    setPreviewUrl,
    onError,
    onDeleted,
  } = props;

  const fetchPDFUrlLatest = useLatest(fetchPDFUrl);
  const { checkReportDeleted } = useReportDeletedCheck({ executable, systemId, studioId, resourceId });
  const isStudioResourceReport = systemId === pageReportTypeConfig.studioReport.systemIdCur;
  const hasWrittenPreviewTokenCookieRef = useRef<boolean>(false);

  const { isCOSInited, getObjectUrl } = useCOS({
    ...cosOptions,
    useAccelerate: COS_USE_ACCELERATE_ENVS.includes(ENV),
  });

  const updateDownloadUrl = async (): Promise<{ isError: boolean; url: string; suffix: string }> => {
    if (!executable) {
      return { isError: false, url: '', suffix: '' };
    }
    let isError = false;
    let url = '';
    let suffix = '';

    try {
      const res = await fetchPDFUrlLatest.current?.();
      const fileData = getFileData(res);
      url = fileData?.url || '';
      suffix = fileData?.suffix || '';
    } catch (error) {
      console.error(error);
      isError = true;
    }

    setFileSuffix(suffix);
    setUrl(url);
    return { isError, url, suffix };
  };

  const updateOriginalFileDownloadUrl = async (): Promise<{
    isError: boolean;
    url: string;
    suffix: string;
  }> => {
    if (!executable || !originalFileKey) {
      return { isError: false, url: '', suffix: '' };
    }

    try {
      const { downloadUrl } = await getObjectUrl({ key: originalFileKey });
      const pureFilePath = originalFileKey.split(/[?#]/)[0];
      const suffix = pureFilePath.substring(pureFilePath.lastIndexOf('.') + 1).toLowerCase();
      return { isError: false, url: downloadUrl || '', suffix };
    } catch (error) {
      console.error(error);
      return { isError: true, url: '', suffix: '' };
    }
  };

  const updateUrl = async () => {
    if (!executable) {
      return;
    }

    const isDeleted = await checkReportDeleted();
    if (isDeleted) {
      onDeleted?.();
      return;
    }

    /**
     * 专区 HTML/ZIP：对齐移动端，iframe 直接指向 util/resource，浏览器跟随 302。
     * 不走 COS blob：zip/html 包内相对资源无法在 blob 沙盒里解析。
     * iframe 带不上 Authorization 头，把 token 写入 cookie 供后端读取。
     */
    if (isStudioResourceReport && resourceId) {
      try {
        setIsLoading(true);
        const token = getAuthorization();
        if (!token) {
          setIsError(true);
          setIsLoading(false);
          onError?.(new Error('Missing authorization token'));
          return;
        }
        writeStudioPreviewTokenCookie(token);
        hasWrittenPreviewTokenCookieRef.current = true;
        setPreviewUrl(`/api/v1/util/resource/${systemId}/${resourceId}/${lang || 'en'}`);
        setIsError(false);
        setIsLoading(false);
      } catch (exception: unknown) {
        console.error(exception);
        setIsError(true);
        setIsLoading(false);
        onError?.(exception);
      }
      return;
    }

    if (!reportFileKey) {
      return;
    }

    try {
      setIsLoading(true);
      const previewUrl = await buildHTMLReportPreviewUrl({ reportFileKey, getObjectUrl });
      setPreviewUrl(previewUrl);
      setIsError(false);
      setIsLoading(false);
    } catch (exception: unknown) {
      console.error(exception);
      setIsError(true);
      setIsLoading(false);
      onError?.(exception);
    }
  };

  const handleReload = (e: React.SyntheticEvent<HTMLElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!executable) {
      return;
    }
    updateUrl();
  };

  useEffect(() => {
    if (!executable) {
      return;
    }
    if (isStudioResourceReport) {
      updateUrl();
      return;
    }
    if (!isCOSInited) {
      return;
    }
    updateUrl();
  }, [executable, isCOSInited, isStudioResourceReport, reportFileKey, resourceId, systemId, lang]);

  useEffect(() => {
    return (): void => {
      if (!hasWrittenPreviewTokenCookieRef.current) {
        return;
      }
      clearStudioPreviewTokenCookie();
      hasWrittenPreviewTokenCookieRef.current = false;
    };
  }, []);

  return { updateDownloadUrl, updateOriginalFileDownloadUrl, onReload: handleReload };
}
